import {
    InterestTypes,
    IProfileChild,
    IProfileOptionsResponse,
    IUserProfileUpdateRequest,
    Persona,
} from '@kcftech/prometheustypescriptapiclient';
import { CollapsiblePane } from 'components/expandable_pane/CollapsiblePane';
import { PaneSection } from 'components/expandable_pane/PaneSection';
import {
    DropdownCustomInputInlineEditor,
    OptionChild,
} from 'components/inline_edit/DropdownCustomInputInlineEditor';
import { MultiSelectInlineEditor } from 'components/inline_edit/MultiSelectInlineEditor';
import { personaIsOther } from 'components/modal/persona/PersonaModal';
import { maxLength } from 'components/modal/persona/PersonaModalPage1';
import { usePermissions } from 'contexts/PermissionsContext';
import { useState } from 'react';

export type PersonaOptionsPaneProps = Readonly<{
    options: IProfileOptionsResponse;
    profile: IProfileChild;
    className?: string;
    onChange: (updates: Partial<IUserProfileUpdateRequest>) => void;
}>;

export function PersonaOptionsPane({
    options: { Personas, PersonaInterests },
    className,
    profile,
    onChange,
}: PersonaOptionsPaneProps) {
    const { canEditUserPersonas } = usePermissions();
    const [expanded, setExpanded] = useState(false);
    const dataOptions: OptionChild<Persona>[] = Personas.map((p) => ({
        Value: p.Value,
        Text: p.Text,
    }));

    if (!canEditUserPersonas) {
        return null;
    }

    return (
        <CollapsiblePane
            expanded={expanded}
            onChange={setExpanded}
            className={className}
            title="Role/Interest"
        >
            <PaneSection label="Your Primary Role">
                <DropdownCustomInputInlineEditor<Persona>
                    customOption={Persona.Other}
                    values={{
                        dropdownValue: profile.Persona,
                        custom: profile.CustomPersona,
                    }}
                    onConfirm={(value) => {
                        onChange({
                            Persona: value?.dropdownValue,
                            CustomPersona: personaIsOther(value?.dropdownValue)
                                ? value.custom
                                : undefined,
                        });
                    }}
                    dropdownConfig={{
                        data: dataOptions,
                        renderLabel: ({ value }) => value?.Text,
                        renderItem: ({ item }) => item.Text,
                        dataKey: (v) => v.Text,
                    }}
                    textInputConfig={{
                        customLabel: 'Specify Your Primary Role',
                        maxLength,
                    }}
                    validate={(value) => {
                        if (personaIsOther(value?.dropdownValue)) {
                            if (!value.custom) {
                                return 'Role is required';
                            }
                            if (value.custom.length > maxLength) {
                                return `Role cannot exceed ${maxLength} characters`;
                            }
                        }
                        return undefined;
                    }}
                />
            </PaneSection>
            <PaneSection label="Areas of Interest">
                <MultiSelectInlineEditor
                    multiSelectConfig={{
                        options: PersonaInterests.map((p) => ({
                            value: InterestTypes[p.Value as keyof typeof InterestTypes],
                            title: p.Text,
                        })),
                        labelPrefersMinWidth: true,
                    }}
                    value={profile.PersonaInterests.map((p) => ({
                        value: p,
                        title:
                            PersonaInterests.find(
                                (i) => i.Value.toString() === p.toString()
                            )?.Text ?? p.toString(),
                    }))}
                    onConfirm={(value) =>
                        onChange({
                            PersonaInterests: value?.map(
                                (v) =>
                                    InterestTypes[v.value as keyof typeof InterestTypes]
                            ),
                        })
                    }
                />
            </PaneSection>
        </CollapsiblePane>
    );
}
